import { findIndex, set } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { MAX_ALLOWED_BTNS } from '@mm/constants/widgets';
import { makeButton } from '../../../editor/features/build/requestModels/widgets/buttonStackWidget/Button';
import buttonCRUDActions from '@mm/state/actions/ButtonCRUDActions';
import { uploadImage } from '@mm/state/actions/uploadActions';
import widgetCRUDActions from '@mm/state/actions/WidgetCRUDActions';
import ButtonModel, {
  ButtonType,
  GenericButtonModel,
  OptInButtonConfig,
} from '@mm/state/model/ButtonModel';
import { UploadImgModel } from '@mm/state/model/UploadImgModel';
import WidgetModel from '@mm/state/model/WidgetModel';
import { StoreState } from '@mm/state/store-interfaces';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';

export const useButtonHandlers = () => {
  const dispatch: Dispatch<StoreState> = useDispatch();
  const chatbotId = useSelector(activeChatbotIdSelector);

  const deleteButton = async (
    widgetId: number,
    pageId: number,
    button: ButtonModel,
  ) => {
    await dispatch(buttonCRUDActions.delete(widgetId, button, false, true));

    dispatch(widgetCRUDActions.retrieveAll(pageId, true));
  };

  const createPathBundle = (
    widget: WidgetModel,
    button: ButtonModel,
    pageGroupId?: number,
    newDialogueName?: string,
  ) => {
    return dispatch(
      buttonCRUDActions.createButtonBundle(
        widget.id,
        {
          name: button.name,
          sortOrder: button.sortOrder,
          type: ButtonType.PAGE,
          bundleType: 'path',
          createInsteadId: button.id,
          pageGroupId,
          newDialogueName,
        },
        chatbotId,
      ),
    );
  };

  const updateText = async (
    widget: WidgetModel,
    button: ButtonModel,
    newButtonText: string,
  ) => {
    const newButton = {
      ...button,
      name: newButtonText,
    };

    await dispatch(buttonCRUDActions.update(widget.id, newButton, false, true));
    dispatch(widgetCRUDActions.retrieveAll(widget.pageId, true));
  };

  const updateSmsResponse = async (
    widget: WidgetModel,
    button: GenericButtonModel<OptInButtonConfig>,
    newSmsResponse: string,
  ) => {
    const newButton = {
      ...button,
      config: { ...button.config, smsResponse: newSmsResponse },
    };

    await dispatch(buttonCRUDActions.update(widget.id, newButton, false, true));
    dispatch(widgetCRUDActions.retrieveAll(widget.pageId, true));
  };

  const deletePathBundle = (widget: WidgetModel, button: ButtonModel) => {
    return dispatch(buttonCRUDActions.deleteButtonBundle(widget.id, button.id));
  };

  const addPostBackButton = async (widget: WidgetModel, name?: string) => {
    if (widget.buttons.length < MAX_ALLOWED_BTNS) {
      const maxSortOrder = Math.max(...widget.buttons.map(b => b.sortOrder), 0);
      const newButton = makeButton({
        type: ButtonType.POSTBACK,
        name: name || '',
        sortOrder: maxSortOrder + 1,
        // @ts-ignore
        configExtendObject: {
          payload: '',
          assetId: null,
        },
      }) as ButtonModel;

      return dispatch(buttonCRUDActions.create(widget.id, newButton));
    }
  };

  const addImageToPostBackButton = async (
    widget: WidgetModel,
    buttonId: number,
    imgBase64: UploadImgModel,
  ) => {
    const buttonIndex = findIndex(
      widget.buttons,
      button => button.id === buttonId,
    );

    if (buttonIndex !== -1) {
      const id = await dispatch(uploadImage(imgBase64));
      const updatedWidget = set(
        widget,
        `buttons.${buttonIndex}.config.assetId`,
        id,
      );

      dispatch(widgetCRUDActions.update(updatedWidget));
    }
  };

  const removeImageFromPostBackButton = (
    widget: WidgetModel,
    buttonId: number,
  ) => {
    const buttonIndex = findIndex(
      widget.buttons,
      button => button.id === buttonId,
    );

    if (buttonIndex !== -1) {
      // TODO: Would immer be a better choice here?
      let updatedWidget = set(
        widget,
        `buttons.${buttonIndex}.config.file`,
        null,
      );
      updatedWidget = set(
        updatedWidget,
        `buttons.${buttonIndex}.config.assetId`,
        null,
      );

      dispatch(widgetCRUDActions.update(updatedWidget));
    }
  };

  const createButton = (widget: WidgetModel, button: Partial<ButtonModel>) => {
    return dispatch(buttonCRUDActions.create(widget.id, button, true));
  };

  // DANGER ZONE:
  // BE changes ID of the button, at least on some udpates
  const updateButton = async (widget: WidgetModel, button: ButtonModel) => {
    const updatedButton = await dispatch(
      buttonCRUDActions.update(widget.id, button, false, true),
    );
    dispatch(widgetCRUDActions.retrieveAll(widget.pageId, true));
    return updatedButton;
  };

  return {
    delete: deleteButton,
    createPathBundle,
    deletePathBundle,
    updateText,
    updateSmsResponse,
    addPostBackButton,
    addImageToPostBackButton,
    removeImageFromPostBackButton,
    createButton,
    updateButton,
  };
};
